import React from 'react';
import { Modal } from '../../../components/common/Modal';
import { formatDecimal } from '../../../utils/format';
import type { RoastedBulkGroup } from '../../../types/inventory';
import { useLanguage } from '../../../features/language/useLanguage';

interface RoastedBulkStockModalProps {
  isOpen: boolean;
  onClose: () => void;
  totalKg: number;
  loading: boolean;
  isError: boolean;
  groups: RoastedBulkGroup[];
}

export const RoastedBulkStockModal: React.FC<RoastedBulkStockModalProps> = ({
  isOpen,
  onClose,
  totalKg,
  loading,
  isError,
  groups,
}) => {
  const { t } = useLanguage();
  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('dashboard.roastedBulkModal.title')} maxWidth="5xl">
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-3">
          <div className="text-sm text-gray-600">
            {t('dashboard.roastedBulkModal.total')}: <span className="font-semibold text-gray-900">{formatDecimal(totalKg, 3)} kg</span>
          </div>
        </div>

        {loading ? (
          <div className="text-sm text-gray-500">{t('dashboard.roastedBulkModal.loading')}</div>
        ) : isError ? (
          <div className="text-sm text-red-600">{t('dashboard.roastedBulkModal.error')}</div>
        ) : groups.length === 0 ? (
          <div className="text-sm text-gray-500">{t('dashboard.roastedBulkModal.empty')}</div>
        ) : (
          <div className="overflow-x-auto border border-gray-100 rounded-xl">
            <table className="min-w-full divide-y divide-gray-100">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">{t('dashboard.roastedBulkModal.item')}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">{t('dashboard.roastedBulkModal.roastType')}</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">{t('dashboard.roastedBulkModal.totalStockKg')}</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {groups.map((g) => (
                  <tr key={`${g.itemId}-${g.roastType}`} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">
                      <div className="font-medium">{g.itemName}</div>
                      <div className="text-xs text-gray-500">{g.itemCode}</div>
                    </td>
                    <td className="uppercase px-4 py-3 text-sm text-gray-700">{g.roastType ? g.roastType : '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 text-right font-semibold">{formatDecimal(g.totalStock, 3)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Modal>
  );
};
